import {
  ADMIN_PERMISSION_ALL_PAGE_KEYS,
  ADMIN_PERMISSION_CATALOG,
  ADMIN_PERMISSION_PAGE_MAP,
} from '@/config/adminPermissionCatalog'
import type { AdminPermissionApiRule, AdminPermissionConfig, AdminUser } from '@/types'

const DEFAULT_VERSION = 1
export const GUILD_SCOPED_ADMIN_PAGE_KEYS = [
  '/anchors/guild-invites',
  '/anchors/pool',
  '/anchors/call-records',
  '/anchors/frame-moderations',
  '/anchors/online-stats',
  '/anchors/withdraw-requests',
  '/anchors/private-media',
  '/anchors/revenue-dashboard',
] as const

const EXEMPT_API_RULES: AdminPermissionApiRule[] = [
  { method: 'GET', path: '/admin-api/v1/admin/auth/profile' },
  { method: 'GET', path: '/admin-api/v1/admin/auth/table-preferences/:key' },
  { method: 'PUT', path: '/admin-api/v1/admin/auth/table-preferences/:key' },
  { method: 'POST', path: '/admin-api/v1/admin/auth/logout' },
  { method: 'PUT', path: '/admin-api/v1/admin/auth/password' },
  { method: 'GET', path: '/admin-api/v1/admin/apps/all' },
]

export function getAllPageKeys() {
  return [...ADMIN_PERMISSION_ALL_PAGE_KEYS]
}

export function getPermissionCatalog() {
  return ADMIN_PERMISSION_CATALOG
}

export function getDefaultAllPermissionConfig(): AdminPermissionConfig {
  return {
    version: DEFAULT_VERSION,
    mode: 'all',
    pages: [],
    apis: [],
  }
}

export function getDefaultCustomPermissionConfig(): AdminPermissionConfig {
  return {
    version: DEFAULT_VERSION,
    mode: 'custom',
    pages: [],
    apis: [],
  }
}

export function parseAdminPermissionConfig(raw?: string | null): AdminPermissionConfig {
  if (!raw?.trim()) {
    return getDefaultAllPermissionConfig()
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AdminPermissionConfig>
    const mode = parsed.mode === 'custom' ? 'custom' : 'all'
    const pages = Array.isArray(parsed.pages)
      ? [...new Set(parsed.pages.map(normalizePermissionPageKey).filter(Boolean))].sort()
      : []
    const apis = Array.isArray(parsed.apis)
      ? dedupeApiRules(
          parsed.apis
            .map((rule) => ({
              method: normalizeMethod(rule?.method),
              path: normalizePath(rule?.path),
            }))
            .filter((rule) => rule.path)
        )
      : []

    if (mode === 'all') {
      return getDefaultAllPermissionConfig()
    }

    return {
      version: DEFAULT_VERSION,
      mode,
      pages,
      apis,
    }
  } catch {
    return getDefaultCustomPermissionConfig()
  }
}

export function buildPermissionConfig(
  mode: 'all' | 'custom',
  selectedPageKeys: string[]
): AdminPermissionConfig {
  if (mode === 'all') {
    return getDefaultAllPermissionConfig()
  }

  const pages = [
    ...new Set(selectedPageKeys.filter((key) => ADMIN_PERMISSION_PAGE_MAP.has(key))),
  ].sort()
  const apiRules = dedupeApiRules(
    pages.flatMap((pageKey) => ADMIN_PERMISSION_PAGE_MAP.get(pageKey)?.apiRules ?? [])
  )

  return {
    version: DEFAULT_VERSION,
    mode: 'custom',
    pages,
    apis: apiRules,
  }
}

export function isSuperAdmin(user?: AdminUser | null) {
  if (!user) return false
  return (
    user.role?.role_name === 'super_admin' ||
    !!user.roles?.some((role) => role.role_name === 'super_admin')
  )
}

// Diagnostic only: persisted roles are never granted new APIs merely by reading the catalog.
export function getMissingPhase1ApiRules(config: AdminPermissionConfig) {
  if (config.mode === 'all') return []
  const phase1Rules = ADMIN_PERMISSION_PAGE_MAP.get('/match/config')?.apiRules.filter(
    (rule) => rule.path.startsWith('/admin-api/v1/admin/match/phase1-profile')
      || rule.path === '/admin-api/v1/admin/match/ai-female-users'
  ) ?? []
  return phase1Rules.filter((required) => !(config.apis ?? []).some(
    (granted) => apiRuleMatches(granted, required.method, required.path)
  ))
}

export function getRoleGuildID(user?: AdminUser | null) {
  return user?.role?.guild_id?.trim() || ''
}

export function hasRoleGuildScope(user?: AdminUser | null) {
  return getRoleGuildID(user) !== ''
}

export function canAccessRoute(user: AdminUser | null | undefined, pathname: string) {
  const definition = resolvePageDefinition(pathname)
  if (!definition) {
    return true
  }
  if (!user || isSuperAdmin(user)) {
    return true
  }

  const config = parseAdminPermissionConfig(user.permissions)
  if (config.mode === 'all') {
    return true
  }

  return config.pages.includes(definition.key)
}

export function isApiAllowedForUser(
  user: AdminUser | null | undefined,
  method: string,
  endpoint: string
) {
  const requestPath = normalizePath(extractRequestPath(endpoint))
  if (!requestPath.startsWith('/admin-api/v1/admin/')) {
    return true
  }
  if (!user || isSuperAdmin(user)) {
    return true
  }

  if (EXEMPT_API_RULES.some((rule) => apiRuleMatches(rule, normalizeMethod(method), requestPath))) {
    return true
  }

  const config = parseAdminPermissionConfig(user.permissions)
  if (config.mode === 'all') {
    return true
  }

  return config.apis.some(
    (rule) =>
      apiRuleMatches(rule, normalizeMethod(method), requestPath) ||
      (normalizeMethod(method) === 'POST' &&
        rule.method === 'GET' &&
        normalizePath(rule.path) === '/admin-api/v1/admin/payment-traces/**' &&
        matchPath('/admin-api/v1/admin/payment-traces/reconciliations/:id/review', requestPath))
  )
}

export function getStoredAdminUser(): AdminUser | null {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = localStorage.getItem('admin_user')
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as AdminUser
  } catch {
    return null
  }
}

export function resolvePageDefinition(pathname: string) {
  const normalized = normalizePath(pathname)
  return ADMIN_PERMISSION_CATALOG.find((item) =>
    item.routePatterns.some((pattern) => matchPath(pattern, normalized))
  )
}

export function getPageKeysBySection(search = '') {
  const keyword = search.trim().toLowerCase()
  return ADMIN_PERMISSION_CATALOG.reduce<Record<string, typeof ADMIN_PERMISSION_CATALOG>>(
    (acc, item) => {
      const searchableText = `${item.section} ${item.label} ${item.aliases?.join(' ') ?? ''} ${item.key}`.toLowerCase()
      if (keyword && !searchableText.includes(keyword)) {
        return acc
      }
      if (!acc[item.section]) {
        acc[item.section] = []
      }
      acc[item.section].push(item)
      return acc
    },
    {}
  )
}

export function matchPath(pattern: string, pathname: string) {
  const normalizedPattern = normalizePath(pattern)
  const normalizedPath = normalizePath(pathname)

  if (!normalizedPattern || !normalizedPath) {
    return false
  }
  if (normalizedPattern === normalizedPath) {
    return true
  }
  if (normalizedPattern.endsWith('/**')) {
    const prefix = normalizedPattern.slice(0, -3)
    return normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  }

  const patternSegments = splitSegments(normalizedPattern)
  const pathSegments = splitSegments(normalizedPath)
  if (patternSegments.length !== pathSegments.length) {
    return false
  }

  return patternSegments.every(
    (segment, index) =>
      segment === pathSegments[index] || segment === '*' || segment.startsWith(':')
  )
}

function apiRuleMatches(rule: AdminPermissionApiRule, method: string, pathname: string) {
  const ruleMethod = normalizeMethod(rule.method)
  if (ruleMethod !== '*' && ruleMethod !== method) {
    return false
  }
  return matchPath(rule.path, pathname)
}

function normalizeMethod(method?: string) {
  return (method || '*').trim().toUpperCase() || '*'
}

function normalizePath(path?: string) {
  if (!path) return ''
  const queryIndex = path.indexOf('?')
  const value = queryIndex >= 0 ? path.slice(0, queryIndex) : path
  if (!value) return ''
  const normalized = value.startsWith('/') ? value : `/${value}`
  return normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized
}

function normalizePermissionPageKey(path?: string) {
  const normalized = normalizePath(path)
  if (
    normalized === '/agent-harness' ||
    normalized.startsWith('/agent-harness/') ||
    normalized === '/observability/agent-harness' ||
    normalized.startsWith('/observability/agent-harness/')
  ) {
    return '/agent-harness'
  }
  return normalized
}

function extractRequestPath(endpoint: string) {
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    try {
      return new URL(endpoint).pathname
    } catch {
      return endpoint
    }
  }
  return endpoint
}

function splitSegments(path: string) {
  if (path === '/') {
    return []
  }
  return path.replace(/^\/+|\/+$/g, '').split('/')
}

function dedupeApiRules(rules: AdminPermissionApiRule[]) {
  const unique = new Map<string, AdminPermissionApiRule>()
  rules.forEach((rule) => {
    const method = normalizeMethod(rule.method)
    const path = normalizePath(rule.path)
    if (!path) return
    unique.set(`${method} ${path}`, { method, path })
  })
  return [...unique.values()].sort((a, b) => {
    if (a.path === b.path) {
      return a.method.localeCompare(b.method)
    }
    return a.path.localeCompare(b.path)
  })
}
